import { Events, on, emit } from './events.js';

class CartDrawer extends HTMLElement {
  connectedCallback() {
    try {
      this.dialogId = this.getAttribute('data-dialog-id') || 'cart';
      this._planCache = Object.create(null);
      this._productCache = Object.create(null);
      this._suppressNextUpdate = false;
      this._buildSequence = 0;
      this._shouldOpenDrawer = false;
      // Template kept outside of #cart-items so SSR content stays intact
      this.template = this.querySelector('#cart-item-template, [data-cart-item-template]')
        || this.closest('store-dialog')?.querySelector('#cart-item-template, [data-cart-item-template]');
      this.itemsEl = this.querySelector('#cart-items');
      this.itemsListEl = this.querySelector('#cart-items-list') || this.itemsEl;
      // Subtotal can live either inside <cart-drawer> or elsewhere in the dialog footer
      this.subtotalEl = this.querySelector('#cart-subtotal') || this.closest('store-dialog')?.querySelector('#cart-subtotal');
      this.subtotalOriginalEl = this.querySelector('#cart-subtotal-original') || this.closest('store-dialog')?.querySelector('#cart-subtotal-original');
      this.addEventListener('click', this.#onClick, true);
      this.addEventListener('change', this.#onChange, true);
      this.addEventListener('focusin', this.#onPlanFocus, true);

      // Discount component is rendered via SSR, no JS rendering needed

      this._unsubs = [
        on(Events.cart.added, () => this.#refresh(true)),
        on(Events.cart.update, () => {
          try {
            if (this._suppressNextUpdate) { this._suppressNextUpdate = false; return; }
          } catch (_) {}
          this.#refresh(false);
        }),
        on(Events.cart.error, (ev) => { try { console.warn('[cart:error]', ev.detail); } catch (_) {} })
      ];
    } catch (_) {}
  }
  #ensureTemplate() {
    try {
      if (this.template && this.template.content) return;
      const found = this.querySelector('#cart-item-template, [data-cart-item-template]')
        || this.closest('store-dialog')?.querySelector('#cart-item-template, [data-cart-item-template]');
      if (found) this.template = found;
    } catch (_) {}
  }

  #ensureDiscountComponent() {
    try {
      if (!this.itemsListEl) return;
      // Check if discount component already exists
      if (this.itemsListEl.querySelector('cart-discount-code')) return;
      // Find the template
      const discountTemplate = this.querySelector('#cart-discount-template, [data-cart-discount-template]')
        || this.closest('store-dialog')?.querySelector('#cart-discount-template, [data-cart-discount-template]');
      if (!discountTemplate || !discountTemplate.content) return;
      // Clone and append
      const clone = discountTemplate.content.cloneNode(true);
      // Insert before upsell provider if present, otherwise append
      const upsellProvider = this.itemsListEl.querySelector('store-upsell-provider');
      if (upsellProvider) {
        this.itemsListEl.insertBefore(clone, upsellProvider);
      } else {
        this.itemsListEl.appendChild(clone);
      }
    } catch (_) {}
  }


  disconnectedCallback() {
    try {
      this.removeEventListener('click', this.#onClick, true);
      this.removeEventListener('change', this.#onChange, true);
      this.removeEventListener('focusin', this.#onPlanFocus, true);
      (this._unsubs || []).forEach((un) => { try { un && un(); } catch (_) {} });
    } catch (_) {}
  }

  #onClick = (ev) => {
    try {
      const btn = ev.target && ev.target.closest && ev.target.closest('[data-action]');
      if (!btn) return;
      ev.preventDefault();
      const action = btn.getAttribute('data-action');
      const row = this.#getRowFromTarget(btn);
      const variantId = row && row.getAttribute('data-variant-id');
      let lineKey = row && row.getAttribute('data-line-key');
      // Do not derive line key from input id; trust SSR data-line-key only
      let planId = '';
      try {
        // Prefer row attribute first (SSR/source of truth), then select value, then data-selected-plan
        planId = (row && row.getAttribute && row.getAttribute('data-plan-id')) || '';
        if (!planId) {
          const sel = row && row.querySelector && row.querySelector('select[data-selling-plan]');
          if (sel) planId = (sel.value || sel.getAttribute('data-selected-plan') || '').trim();
        }
      } catch (_) { planId = ''; }
      const qtyInput = row && row.querySelector('input[name="quantity"]');
      const currentQty = qtyInput ? parseInt(qtyInput.value, 10) || 0 : 0;
      try { if (window.store && window.store.debug) window.store.log('[cart-drawer] click', { action, variantId, lineKey, planId, currentQty }); } catch (_) {}

      if (!variantId) return;
      if (action === 'inc') return this.#withButtonLoading(btn, () => this.#changeQuantity({ variantId, lineKey, quantity: currentQty + 1, planId }));
      if (action === 'dec') return this.#withButtonLoading(btn, () => this.#changeQuantity({ variantId, lineKey, quantity: Math.max(0, currentQty - 1), planId }));
      if (action === 'remove') return this.#changeQuantity({ variantId, lineKey, quantity: 0, planId });
    } catch (_) {}
  };

  async #changeQuantity({ variantId, lineKey, quantity, planId }) {
    try {
      let data;
      // Prefer direct key-based change when available (most reliable)
      if (lineKey && lineKey !== '') {
        const res = await fetch('/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: Number(quantity) })
        });
        data = await res.json();
        try { if (window.store && window.store.debug) window.store.log('[cart-drawer] changeQuantity: used key', { lineKey, nextQty: quantity, result: data }); } catch (_) {}
      } else {
        // Resolve exact line index to avoid variant-only updates affecting the wrong line
        let lineIndex = null;
        try {
          // Prefer current global state; if absent, fetch fresh
          let cart = (window.store && window.store.cartState) || null;
          if (!cart || !Array.isArray(cart.items)) {
            const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
            cart = await res.json();
          }
          try { if (window.store && window.store.debug) window.store.log('[cart-drawer] changeQuantity: cart snapshot', { items: (cart.items||[]).map((it,i)=>({i:i+1, key:it.key, vid:it.variant_id, qty:it.quantity, plan: it.selling_plan_allocation && it.selling_plan_allocation.selling_plan && it.selling_plan_allocation.selling_plan.id })) }); } catch (_) {}
          if (cart && Array.isArray(cart.items)) {
            const vid = Number(variantId);
            const pid = (planId && String(planId).trim() !== '') ? String(planId) : '';
            const idx = cart.items.findIndex((it) => {
              try {
                if (!it) return false;
                if (Number(it.variant_id) !== vid) return false;
                const itPlan = (it.selling_plan_allocation && it.selling_plan_allocation.selling_plan && it.selling_plan_allocation.selling_plan.id) ? String(it.selling_plan_allocation.selling_plan.id) : '';
                return itPlan === pid;
              } catch (_) { return false; }
            });
            if (idx >= 0) lineIndex = idx + 1; // 1-based
          }
        } catch (_) { lineIndex = null; }
        try { if (window.store && window.store.debug) window.store.log('[cart-drawer] changeQuantity: resolve', { variantId, lineKey, planId, quantity, lineIndex }); } catch (_) {}

        if (lineIndex != null) {
          const res = await fetch('/cart/change.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ line: Number(lineIndex), quantity: Number(quantity) })
          });
          data = await res.json();
          try { if (window.store && window.store.debug) window.store.log('[cart-drawer] changeQuantity: used line index', { lineIndex, nextQty: quantity, result: data }); } catch (_) {}
        } else {
          // No safe target found: do not attempt a variant-wide update; refresh instead
          try { if (window.store && window.store.debug) window.store.log('[cart-drawer] changeQuantity: no line match; refresh only', { variantId, planId, quantity }); } catch (_) {}
          await this.#refresh(true);
          return;
        }
      }
      emit(Events.cart.update, { cart: data }, this);
      await this.#buildFromCart(data, false);
      this.#open();
    } catch (e) {
      emit(Events.cart.error, { message: e && e.message }, this);
    }
  }

  async #withButtonLoading(button, task) {
    try {
      if (!button || typeof task !== 'function') return await task();
      const row = button.closest('[data-variant-id]');
      const decBtn = row && row.querySelector('[data-action="dec"]');
      const incBtn = row && row.querySelector('[data-action="inc"]');
      const allBtns = [decBtn, incBtn].filter(Boolean);
      const qtyInput = row && row.querySelector('input[name="quantity"]');
      const otherBtn = (button === incBtn ? decBtn : incBtn) || null;

      // Disable both buttons; replace icon with spinner only on the triggering button
      allBtns.forEach((b) => {
        try { b.disabled = true; } catch (_) {}
      });
      // Hide the icon span via inline style to avoid Tailwind cascade conflicts
      let __iconRef = null;
      try {
        const icon = button.querySelector('[data-role="icon"]');
        if (icon) { __iconRef = icon; icon.style.display = 'none'; }
      } catch (_) { __iconRef = null; }
      // Disable the quantity input during the async operation
      try { if (qtyInput) qtyInput.disabled = true; } catch (_) {}

      // Show spinner on the triggering button only
      const spin = button.querySelector('[data-role="spinner"]');
      if (spin) { spin.classList.remove('hidden'); try { spin.style.display = 'inline-flex'; } catch (_) {} }
      try {
        return await task();
      } finally {
        // Restore buttons and input
        if (spin) { spin.classList.add('hidden'); try { spin.style.display = ''; } catch (_) {} }
        allBtns.forEach((b) => {
          try {
            const i = b.querySelector('[data-role="icon"]');
            if (i) { try { i.style.display = ''; } catch (_) {} }
            b.disabled = false;
          } catch (_) {}
        });
        // Opposite button remains visible; nothing to unhide there
        try { if (qtyInput) qtyInput.disabled = false; } catch (_) {}
      }
    } catch (e) { throw e; }
  }

  async #update(updates) {
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates })
      });
      const data = await res.json();
      emit(Events.cart.update, { cart: data }, this);
      await this.#buildFromCart(data, false);
      this.#open();
    } catch (e) {
      emit(Events.cart.error, { message: e && e.message }, this);
    }
  }

  // removed optimistic helpers; rely on server response only

  async #refresh(openAfter = true) {
    try {
      const cart = await this.#fetchCart();
      await this.#buildFromCart(cart, openAfter);
    } catch (_) {}
  }

  async #fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return await res.json();
  }

  async #buildFromCart(cart, openAfter) {
    try {
      if (!this.itemsListEl) return;
      const requestId = (this._buildSequence = (this._buildSequence || 0) + 1);
      if (openAfter) this._shouldOpenDrawer = true;

      // Late-bind the template in case connectedCallback ran before children were parsed
      this.#ensureTemplate();

      if (!cart || !cart.items || !cart.items.length) {
        // Empty cart - clear and show empty state
        let emptyText = 'Your cart is empty.';
        try { emptyText = this.getAttribute('data-empty-text') || emptyText; } catch (_) {}
        try {
          const inline = this.querySelector('[data-empty-text]');
          const t = inline && inline.textContent ? String(inline.textContent).trim() : '';
          if (t) emptyText = t;
        } catch (_) {}
        try {
          const localAttr = this.getAttribute('data-i18n-cart-empty');
          if (localAttr) emptyText = localAttr;
        } catch (_) {}
        try {
          const dialogAttr = this.closest('store-dialog')?.getAttribute('data-i18n-cart-empty');
          if (dialogAttr) emptyText = dialogAttr;
        } catch (_) {}
        try {
          const global = document.documentElement.getAttribute('data-i18n-cart-empty');
          if (global) emptyText = global;
        } catch (_) {}
        // Preserve persistent components when clearing cart
        const divider = this.itemsListEl.querySelector('[data-cart-divider]');
        const upsellHeading = this.itemsListEl.querySelector('[data-upsell-heading]');
        const upsellProvider = this.itemsListEl.querySelector('store-upsell-provider');
        const discountCode = this.itemsListEl.querySelector('cart-discount-code');
        const persistentEls = [divider, upsellHeading, upsellProvider, discountCode].filter(Boolean);

        if (persistentEls.length > 0) {
          // Remove all children except persistent elements, then prepend empty state
          const children = Array.from(this.itemsListEl.children);
          children.forEach((child) => {
            try { if (!persistentEls.includes(child)) child.remove(); } catch (_) {}
          });
          const emptyEl = document.createElement('div');
          emptyEl.innerHTML = this.#renderEmptyState(emptyText);
          // Insert before first persistent element
          this.itemsListEl.insertBefore(emptyEl.firstElementChild, persistentEls[0]);
        } else {
          this.itemsListEl.innerHTML = this.#renderEmptyState(emptyText);
        }
      } else {
        // Reconciliation: update existing items, add new, remove deleted
        await this.#primeProductData(cart.items);
        if (requestId !== this._buildSequence) return;

        // Build map of existing DOM items by line key
        const existing = new Map();
        this.itemsListEl.querySelectorAll('[data-line-key]').forEach((el) => {
          try {
            const key = el.getAttribute('data-line-key');
            if (key) existing.set(key, el);
          } catch (_) {}
        });

        // Remove empty state if present
        const emptyState = this.itemsListEl.querySelector('[data-empty-state]');
        if (emptyState) emptyState.remove();

        // Track which keys are in the new cart
        const newKeys = new Set();
        const itemsOrder = [];

        // Reverse items so newest appears at end (Shopify returns newest first)
        const orderedItems = [...cart.items].reverse();

        orderedItems.forEach((item) => {
          try {
            const key = item.key;
            newKeys.add(key);

            if (existing.has(key)) {
              // Update existing item in place
              const el = existing.get(key);
              this.#updateItemElement(el, item);
              itemsOrder.push(el);
            } else {
              // Render new item
              const newEl = this.#renderItem(item);
              itemsOrder.push(newEl);
            }
          } catch (_) {}
        });

        if (requestId !== this._buildSequence) return;

        // Remove items no longer in cart
        existing.forEach((el, key) => {
          try {
            if (!newKeys.has(key)) el.remove();
          } catch (_) {}
        });

        // Ensure correct order by inserting items before divider/upsell provider
        const frag = document.createDocumentFragment();
        itemsOrder.forEach((el) => {
          try { frag.appendChild(el); } catch (_) {}
        });
        // Insert before divider if present, otherwise before upsell provider, otherwise append
        const divider = this.itemsListEl.querySelector('[data-cart-divider]');
        const upsellProvider = this.itemsListEl.querySelector('store-upsell-provider');
        if (divider) {
          this.itemsListEl.insertBefore(frag, divider);
        } else if (upsellProvider) {
          this.itemsListEl.insertBefore(frag, upsellProvider);
        } else {
          this.itemsListEl.appendChild(frag);
        }
      }

      if (requestId !== this._buildSequence) return;
      if (this.subtotalEl) this.subtotalEl.textContent = this.#formatMoney(cart && cart.total_price);
      try {
        if (requestId !== this._buildSequence) return;
        if (this.subtotalOriginalEl) {
          const original = this.#computeOriginalSubtotal(cart);
          if (original > ((cart && cart.total_price) || 0)) {
            this.subtotalOriginalEl.textContent = this.#formatMoney(original);
            try { this.subtotalOriginalEl.removeAttribute('hidden'); } catch (_) {}
          } else {
            try { this.subtotalOriginalEl.setAttribute('hidden',''); } catch (_) {}
          }
        }
      } catch (_) {}
      if (requestId !== this._buildSequence) return;
      try {
        const countEl = this.closest('store-dialog')?.querySelector('[data-role="cart-count"]');
        if (countEl) countEl.textContent = `(${(cart && cart.item_count) || 0})`;
      } catch (_) {}
      if (requestId !== this._buildSequence) return;
      // Shipping bar updates are handled by <store-shipping-bar> reacting to cart events
      // Pre-hydrate plan options so selection reflects current line state (SSR fallback friendly)
      try {
        const selects = this.itemsListEl.querySelectorAll('select[data-selling-plan]');
        selects.forEach((s) => { try { this.#ensurePlanOptions(s); } catch (_) {} });
        // Sync quantity inputs to reflect current cart values post-refresh
        const rows = this.itemsListEl.querySelectorAll('[data-variant-id]');
        rows.forEach((row) => {
          try {
            const qtyInput = row.querySelector('input[name="quantity"]');
            const qtyText = row.querySelector('[data-role="qty"]');
            if (qtyInput && qtyText) qtyText.textContent = qtyInput.value;
            // Hide plan name when empty (template renders wrapper in token mode)
            const planNameEl = row.querySelector('[data-role="plan-name"]');
            if (planNameEl) {
              const text = (planNameEl.textContent || '').trim();
              if (!text) planNameEl.setAttribute('hidden', '');
              else planNameEl.removeAttribute('hidden');
            }
          } catch (_) {}
        });
      } catch (_) {}
      if (requestId !== this._buildSequence) return;
      if (this._shouldOpenDrawer) {
        this._shouldOpenDrawer = false;
        this.#open();
      }
    } catch (_) {}
  }

  #updateItemElement(el, item) {
    try {
      // Update data attributes
      el.setAttribute('data-quantity', String(item.quantity));
      el.setAttribute('data-variant-id', String(item.id));

      // Update quantity display
      const qtyInput = el.querySelector('input[name="quantity"]');
      if (qtyInput) qtyInput.value = String(item.quantity);
      const qtyText = el.querySelector('[data-role="qty"]');
      if (qtyText) qtyText.textContent = String(item.quantity);

      // Update quantity in title (e.g., "2x Product Name")
      const titleEl = el.querySelector('h3');
      if (titleEl) {
        const qtySpan = titleEl.querySelector('span');
        if (qtySpan) qtySpan.textContent = `${item.quantity}x`;
      }

      // Update prices
      const unitFinal = this.#computeUnitFinal(item);
      const unitOriginal = this.#computeUnitOriginal(item);
      const unitSaved = this.#computeUnitSaved(item);
      const lineSaved = this.#computeLineSaved(item);

      el.setAttribute('data-unit-saved', String(Math.max(0, Math.round(unitSaved))));
      el.setAttribute('data-line-saved', String(Math.max(0, Math.round(lineSaved))));

      const priceEl = el.querySelector('[data-role="price"]');
      if (priceEl) priceEl.innerHTML = this.#formatMoneyHtml(unitFinal);

      const priceOriginalEl = el.querySelector('[data-role="price-original"]');
      const saveBadgeEl = el.querySelector('[data-role="save-badge"]');

      if (unitSaved > 0) {
        if (priceOriginalEl) {
          priceOriginalEl.innerHTML = this.#formatMoneyHtml(unitOriginal);
          priceOriginalEl.removeAttribute('hidden');
        }
        if (saveBadgeEl) {
          const moneySpan = saveBadgeEl.querySelector('.money');
          if (moneySpan) {
            // Strip HTML wrapper if Shopify.formatMoney returns <span class=money>...</span>
            const formatted = this.#formatMoney(unitSaved);
            const stripped = formatted.replace(/<span[^>]*class=["']?money["']?[^>]*>(.*?)<\/span>/gi, '$1');
            moneySpan.textContent = stripped;
          }
          saveBadgeEl.removeAttribute('hidden');
        }
      } else {
        if (priceOriginalEl) priceOriginalEl.setAttribute('hidden', '');
        if (saveBadgeEl) saveBadgeEl.setAttribute('hidden', '');
      }

      // Update plan name if present
      const planNameEl = el.querySelector('[data-role="plan-name"]');
      if (planNameEl) {
        const planName = (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.name) || '';
        if (planName) {
          planNameEl.textContent = planName;
          planNameEl.removeAttribute('hidden');
        } else {
          planNameEl.setAttribute('hidden', '');
        }
      }

      // Update plan select
      const planSelect = el.querySelector('select[data-selling-plan]');
      if (planSelect) {
        const planId = (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id) || '';
        planSelect.setAttribute('data-selected-plan', String(planId));
        el.setAttribute('data-plan-id', String(planId));
      }
    } catch (_) {}
  }

  #renderEmptyState(text) {
    try {
      const safe = this.#escape(text || '');
      return '<article class="cart-item flex flex-col items-start gap-5 px-[0.9rem] lg:px-[1.8rem]" data-empty-state="true"><p class="text-sm text-[color:var(--muted,#666)]">' + safe + '</p></article>';
    } catch (_) {
      return '<article class="cart-item flex flex-col items-start gap-5 px-[0.9rem] lg:px-[1.8rem]" data-empty-state="true"><p class="text-sm text-[color:var(--muted,#666)]">' + (text || '') + '</p></article>';
    }
  }

  #renderItem(item) {
    try {
      this.#ensureTemplate();
      const tpl = this.template && this.template.content ? this.template.content.cloneNode(true) : document.createDocumentFragment();
      const wrap = document.createElement('div');
      wrap.appendChild(tpl);
      wrap.innerHTML = wrap.innerHTML
        .replace(/\$\$VARIANT_ID\$\$/g, String(item.id))
        .replace(/\$\$ITEM_QUANTITY\$\$/g, String(item.quantity))
        .replace(/\$\$ITEM_TITLE\$\$/g, this.#escape(item.product_title))
        .replace(/\$\$ITEM_VARIANT_TITLE\$\$/g, item.variant_title ? this.#escape(item.variant_title) : '')
        .replace(/\$\$ITEM_PRICE\$\$/g, this.#formatMoneyHtml(this.#computeLineFinal(item)))
        .replace(/\$\$IMAGE_URL\$\$/g, item.image ? `${item.image}&width=320` : 'https://via.placeholder.com/320')
        .replace(/\$\$IMAGE_ALT\$\$/g, item.image && item.image.alt ? this.#escape(item.image.alt) : '')
        .replace(/\$\$LINE_KEY\$\$/g, this.#escape(item.key || ''))
        .replace(/\$\$HANDLE\$\$/g, this.#escape(item.handle || ''))
        .replace(/\$\$SELECTED_PLAN_ID\$\$/g, (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id) ? String(item.selling_plan_allocation.selling_plan.id) : '')
        .replace(/\$\$SELECTED_PLAN_NAME\$\$/g, (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.name) ? this.#escape(item.selling_plan_allocation.selling_plan.name) : '');
      // Unit price + savings tokens used by the template (include selling plan compare_at_price)
      const unitFinal = this.#computeUnitFinal(item);
      const unitOriginal = this.#computeUnitOriginal(item);
      const unitSaved = this.#computeUnitSaved(item);
      const lineSaved = this.#computeLineSaved(item);
      const hideSavings = unitSaved > 0 ? '' : 'hidden';
      wrap.innerHTML = wrap.innerHTML
        .replace(/\$\$UNIT_PRICE_FINAL\$\$/g, this.#formatMoneyHtml(unitFinal))
        .replace(/\$\$UNIT_PRICE_ORIGINAL\$\$/g, this.#formatMoneyHtml(unitOriginal))
        .replace(/\$\$UNIT_SAVED\$\$/g, this.#formatMoneyHtml(unitSaved))
        .replace(/\$\$UNIT_SAVED_CENTS\$\$/g, String(Math.max(0, Math.round(unitSaved))))
        .replace(/\$\$LINE_SAVED_CENTS\$\$/g, String(Math.max(0, Math.round(lineSaved))))
        .replace(/\$\$HIDE_IF_NO_SAVINGS\$\$/g, hideSavings)
        .replace(/\$\$hide_if_no_savings\$\$/g, hideSavings);
      // Remove savings UI completely when there is no discount
      try {
        if (unitSaved <= 0) {
          wrap.querySelectorAll('[data-role="save-badge"], [data-role="price-original"]').forEach((el) => { try { el.remove(); } catch (_) {} });
        }
      } catch (_) {}
      try {
        const rowEl = wrap.querySelector('[data-variant-id]');
        if (rowEl) {
          rowEl.setAttribute('data-unit-saved', String(Math.max(0, Math.round(unitSaved))));
          rowEl.setAttribute('data-line-saved', String(Math.max(0, Math.round(lineSaved))));
        }
      } catch (_) {}
      const frag = document.createDocumentFragment();
      Array.from(wrap.childNodes).forEach((n) => frag.appendChild(n));
      return frag;
    } catch (_) { return document.createDocumentFragment(); }
  }

  #renderDiscountCode(cart) {
    try {
      const discountTemplate = document.querySelector('#cart-discount-template');
      if (!discountTemplate || !discountTemplate.content) return null;

      const tpl = discountTemplate.content.cloneNode(true);
      const wrap = document.createElement('div');
      wrap.appendChild(tpl);

      // Replace tokens with cart data
      const discountCodesJSON = JSON.stringify(cart.discount_codes || []);
      const currency = cart.currency || 'EUR';
      const discountApplicationsJSON = JSON.stringify(cart.cart_level_discount_applications || []);

      wrap.innerHTML = wrap.innerHTML
        .replace(/\$\$DISCOUNT_CODES\$\$/g, discountCodesJSON.replace(/"/g, '&quot;'))
        .replace(/\$\$STORE_CURRENCY\$\$/g, currency)
        .replace(/\$\$DISCOUNT_APPLICATIONS\$\$/g, discountApplicationsJSON.replace(/"/g, '&quot;'));

      const frag = document.createDocumentFragment();
      Array.from(wrap.childNodes).forEach((n) => frag.appendChild(n));
      return frag;
    } catch (_) { return null; }
  }

  #onChange = (ev) => {
    try {
      const target = ev.target;
      // Selling plan select change
      const select = target && target.closest && target.closest('select[data-selling-plan]');
      if (select) {
        ev.preventDefault();
        const row = this.#getRowFromTarget(select);
        const variantId = row && row.getAttribute('data-variant-id');
        const planId = select.value && select.value !== '' ? Number(select.value) : null;
        if (!variantId) return;
        const qtyInput = row && row.querySelector('input[name="quantity"]');
        const currentQty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        const lineKey = row.getAttribute('data-line-key');
        try { if (window.store && window.store.debug) window.store.log('[cart-drawer] plan change', { variantId, lineKey, planId, currentQty }); } catch (_) {}
        this.#changeLinePlan({ variantId, quantity: currentQty, planId, lineKey });
        return;
      }
      // Quantity input change
      const qty = target && target.matches && target.matches('input[name="quantity"]') ? target : null;
      if (qty) {
        ev.preventDefault();
        const row = this.#getRowFromTarget(qty);
        const variantId = row && row.getAttribute('data-variant-id');
        let lineKey = row && row.getAttribute('data-line-key');
        // Do not derive line key from input id; trust SSR data-line-key only
        let planId = '';
        try {
          planId = (row && row.getAttribute && row.getAttribute('data-plan-id')) || '';
          if (!planId) {
            const sel = row && row.querySelector && row.querySelector('select[data-selling-plan]');
            if (sel) planId = (sel.value || sel.getAttribute('data-selected-plan') || '').trim();
          }
        } catch (_) { planId = ''; }
        if (!variantId) return;
        let next = parseInt(qty.value, 10) || 0;
        const min = parseInt(qty.getAttribute('min') || '0', 10);
        const max = parseInt(qty.getAttribute('max') || '9999', 10);
        if (Number.isFinite(min)) next = Math.max(min, next);
        if (Number.isFinite(max)) next = Math.min(max, next);
        // Do not update the input value yet; wait for server response to rebuild UI
        try { if (window.store && window.store.debug) window.store.log('[cart-drawer] qty input', { variantId, lineKey, planId, next }); } catch (_) {}
        // show spinner on both +/- if present until server returns
        const decBtn = row.querySelector('[data-action="dec"]');
        const incBtn = row.querySelector('[data-action="inc"]');
        const run = () => this.#changeQuantity({ variantId, lineKey, quantity: next, planId });
        if (incBtn) this.#withButtonLoading(incBtn, run); else if (decBtn) this.#withButtonLoading(decBtn, run); else run();
      }
    } catch (_) {}
  };

  #getRowFromTarget(el) {
    try {
      // Prefer explicit cart row article with data-line-key
      const byArticle = el && el.closest && el.closest('article[data-line-key]');
      if (byArticle) return byArticle;
      // Use composedPath to guard against nested clickable elements
      const path = (typeof el.composedPath === 'function') ? el.composedPath() : [];
      for (let i = 0; i < path.length; i += 1) {
        const n = path[i];
        try { if (n && n.nodeType === 1 && n.matches && n.matches('article[data-line-key]')) return n; } catch (_) {}
      }
      // Fallbacks
      const byKey = el && el.closest && el.closest('[data-line-key]');
      if (byKey) return byKey;
      const byVid = el && el.closest && el.closest('article[data-variant-id]');
      if (byVid) return byVid;
      return el && el.closest && el.closest('[data-variant-id]');
    } catch (_) { return (el && el.closest && (el.closest('[data-line-key]') || el.closest('[data-variant-id]'))) || null; }
  }

  async #changeLinePlan({ variantId, quantity, planId, lineKey }) {
    try {
      // Remove the specific line via change.js (using line key) if available
      if (lineKey) {
        await fetch('/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: 0 })
        });
      } else {
        await fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates: { [variantId]: 0 } })
        });
      }
      const addRes = await fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Number(quantity), selling_plan: planId || undefined }] })
      });
      await addRes.json();
      // Fetch full, authoritative cart after mutation to ensure all listeners receive correct totals/state
      let fullCart = null;
      try {
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        fullCart = await res.json();
      } catch (_) { fullCart = null; }
      if (fullCart) {
        // Prevent our own update handler from triggering an extra refresh
        this._suppressNextUpdate = true;
        emit(Events.cart.update, { cart: fullCart }, this);
        await this.#buildFromCart(fullCart, true);
      } else {
        await this.#refresh(true);
      }
    } catch (e) {
      emit(Events.cart.error, { message: e && e.message }, this);
    }
  }

  #onPlanFocus = async (ev) => {
    try {
      const select = ev.target && ev.target.closest && ev.target.closest('select[data-selling-plan]');
      if (!select) return;
      this.#ensurePlanOptions(select);
    } catch (_) {}
  }

  // removed optimistic input handler; rely on server response for state

  async #primeProductData(items) {
    try {
      if (!Array.isArray(items) || !items.length) return;
      if (!this._productCache) this._productCache = Object.create(null);
      const handles = new Set();
      items.forEach((it) => {
        try {
          const handle = it && it.handle;
          if (!handle) return;
          const cached = this._productCache[handle];
          if (cached === undefined) handles.add(handle);
        } catch (_) {}
      });
      if (!handles.size) return;
      await Promise.all(Array.from(handles).map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}.js`, { headers: { 'Accept': 'application/json' } });
          const data = await res.json();
          this._productCache[handle] = data;
        } catch (_) {
          this._productCache[handle] = false;
        }
      }));
    } catch (_) {}
  }

  #ensurePlanOptions = async (select) => {
    try {
      if (!select || select.getAttribute('data-hydrated') === 'true') return;
      const row = select.closest('[data-variant-id]');
      const handle = row && row.getAttribute('data-handle');
      if (!handle) return;
      let data = this._planCache[handle];
      if (!data) {
        const res = await fetch(`/products/${handle}.js`, { headers: { 'Accept': 'application/json' } });
        data = await res.json();
        this._planCache[handle] = data;
        if (this._productCache) this._productCache[handle] = data;
      }
      const groups = (data && data.selling_plan_groups) || [];
      const selected = select.getAttribute('data-selected-plan') || '';
      const opts = [];
      groups.forEach((g) => {
        (g.selling_plans || []).forEach((p) => {
          const id = Number(p.id);
          const name = p.name || `Plan ${id}`;
          const sel = String(id) === String(selected) ? ' selected' : '';
          opts.push(`<option value="${id}"${sel}>${name}</option>`);
        });
      });
      // If no plans, leave as one-time only
      if (!opts.length) { select.setAttribute('data-hydrated', 'true'); return; }
      // Preserve existing SSR one-time text if present; else fall back to html[data-i18n-cart-one-time] or English
      let oneTimeText = 'One-time purchase';
      try {
        const ssrFirst = select.querySelector('option[value=""]') || (select.options && select.options[0]);
        const txt = ssrFirst && (ssrFirst.textContent || ssrFirst.innerText);
        if (txt && String(txt).trim()) oneTimeText = String(txt).trim();
      } catch (_) {}
      try {
        const dialogOneTime = this.closest('store-dialog')?.getAttribute('data-i18n-cart-one-time');
        if (dialogOneTime) oneTimeText = dialogOneTime;
      } catch (_) {}
      try { oneTimeText = document.documentElement.getAttribute('data-i18n-cart-one-time') || oneTimeText; } catch (_) {}
      const first = `<option value="">${this.#escape(oneTimeText)}</option>`;
      select.innerHTML = first + opts.join('');
      // Ensure exact selection
      if (selected && opts.length) select.value = String(selected);
      select.setAttribute('data-hydrated', 'true');
    } catch (_) {}
  }

  // Removed legacy shipping bar updater; component listens to cart events instead

  #formatMoney(cents) {
    try {
      const value = typeof cents === 'number' ? Math.round(cents) : parseInt(String(cents||0), 10) || 0;
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        const fmt = window.Shopify.money_format || undefined;
        return window.Shopify.formatMoney(value, fmt);
      }
      // Fallback: no currency prefix; rely on shop format in Liquid for SSR
      return (value/100).toFixed(2);
    } catch (_) {
      try { return (Number(cents||0)/100).toFixed(2); } catch (__) { return String(cents); }
    }
  }

  #formatMoneyHtml(cents) {
    try {
      return '<span class="money">' + this.#formatMoney(cents) + '</span>';
    } catch (_) {
      try { return '<span class="money">' + (Number(cents || 0) / 100).toFixed(2) + '</span>'; } catch (__) { return '<span class="money">' + String(cents) + '</span>'; }
    }
  }

  #computeUnitSaved(item) {
    try {
      if (!item) return 0;
      const unitFinal = this.#computeUnitFinal(item);
      const unitOrig = this.#computeUnitOriginal(item);
      const diff = Number(unitOrig) - Number(unitFinal);
      return diff > 0 ? diff : 0;
    } catch (_) { return 0; }
  }

  #computeLineSaved(item) {
    try {
      if (!item) return 0;
      const qty = Number(item && item.quantity) || 0;
      const unitSaved = this.#computeUnitSaved(item);
      const savedViaUnits = unitSaved > 0 ? unitSaved * (qty || 1) : 0;
      if (savedViaUnits > 0) return savedViaUnits;
      const finalLine = Number((item && (item.final_line_price != null ? item.final_line_price : item.line_price)) || 0);
      const origLine = Number((item && item.original_line_price != null ? item.original_line_price : finalLine) || 0);
      const diff = origLine - finalLine;
      return diff > 0 ? diff : 0;
    } catch (_) { return 0; }
  }

  #computeLineFinal(item) {
    try { return Number((item && (item.final_line_price != null ? item.final_line_price : item.line_price)) || 0); } catch (_) { return 0; }
  }

  #computeUnitFinal(item) {
    try { return (item && (item.final_price != null ? item.final_price : item.price)) || 0; } catch (_) { return 0; }
  }

  #computeUnitOriginal(item) {
    try {
      if (!item) return 0;
      let orig = (item.original_price != null ? Number(item.original_price) : this.#computeUnitFinal(item)) || 0;
      const plan = item && item.selling_plan_allocation;
      const planCompare = plan && plan.compare_at_price != null ? Number(plan.compare_at_price) : 0;
      if (planCompare > 0) {
        if (planCompare > orig) orig = planCompare;
      } else {
        const cmp = this.#resolveCompareAt(item);
        if (cmp > orig) orig = cmp;
      }
      return orig;
    } catch (_) { return this.#computeUnitFinal(item); }
  }

  #computeOriginalSubtotal(cart) {
    try {
      if (!cart || !Array.isArray(cart.items)) return Number(cart && cart.total_price) || 0;
      let sum = 0;
      (cart.items || []).forEach((it) => {
        try {
          const qty = Number(it && it.quantity) || 0;
          const finalLine = Number((it && (it.final_line_price != null ? it.final_line_price : it.line_price)) || 0);
          const fallbackOrig = Number((it && it.original_line_price != null ? it.original_line_price : finalLine) || 0);
          let origLine = finalLine + this.#computeLineSaved(it);
          if (origLine < fallbackOrig) origLine = fallbackOrig;
          if (origLine < finalLine) origLine = finalLine;
          sum += origLine;
        } catch (_) {}
      });
      return sum;
    } catch (_) { return Number(cart && cart.total_price) || 0; }
  }

  #resolveCompareAt(item) {
    try {
      const sources = [];
      if (item && item.compare_at_price != null) sources.push(item.compare_at_price);
      if (item && item.variant_compare_at_price != null) sources.push(item.variant_compare_at_price);
      if (item && item.variant && item.variant.compare_at_price != null) sources.push(item.variant.compare_at_price);
      if (item && item.product && item.product.compare_at_price != null) sources.push(item.product.compare_at_price);
      if (item && item.product && item.product.compare_at_price_max != null) sources.push(item.product.compare_at_price_max);
      const handle = item && item.handle;
      let productData = null;
      if (handle && this._productCache && this._productCache[handle]) productData = this._productCache[handle];
      else if (handle && this._planCache && this._planCache[handle]) productData = this._planCache[handle];
      if (productData && productData !== false) {
        try {
          if (productData.compare_at_price_max != null) sources.push(productData.compare_at_price_max);
          if (productData.compare_at_price != null) sources.push(productData.compare_at_price);
          const variants = productData.variants || [];
          const vid = Number(item && (item.variant_id != null ? item.variant_id : item.id));
          variants.forEach((variant) => {
            try {
              if (!variant) return;
              if (vid && Number(variant.id) !== vid) return;
              if (variant.compare_at_price != null) sources.push(variant.compare_at_price);
            } catch (_) {}
          });
        } catch (_) {}
      }
      for (let i = 0; i < sources.length; i += 1) {
        const raw = sources[i];
        const value = Number(raw);
        if (Number.isFinite(value) && value > 0) return Math.round(value);
      }
      return 0;
    } catch (_) { return 0; }
  }

  #escape(s) {
    try { const d = document.createElement('div'); d.textContent = String(s || ''); return d.innerHTML; } catch (_) { return String(s || ''); }
  }

  #open() {
    try {
      const id = this.dialogId;
      if (!id) return;
      if (window.store && window.store.dialog && window.store.dialog.open) window.store.dialog.open(id);
      else document.getElementById(id)?.setAttribute('data-open','');
    } catch (_) {}
  }
}

try { if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer); } catch (_) {}


